// src/pages/gene.codes/bloods/BloodAnalysiscomponents/BloodAnalysisStateNotice.tsx
// Single source of truth for the non-completed AI-analysis narrative states
// (processing / not analysed / failed / not available). Callers render the real
// narrative themselves once analysis.status === 'completed' — this component is
// only used for the other cases so the copy lives in one place.
import React from "react";
import { Typography } from "antd";
import { WarningOutlined, InfoCircleOutlined } from "@ant-design/icons";

import { EmptyState, InlineLoading } from "../../../../components/shared";
import { BloodTestAnalysis } from "../../../../types/lab/BloodTestTypes";

const { Text } = Typography;

interface BloodAnalysisStateNoticeProps {
	analysis?: BloodTestAnalysis | null;
}

const BloodAnalysisStateNotice: React.FC<BloodAnalysisStateNoticeProps> = ({ analysis }) => {
	// No analysis row at all — neutral "not available" panel.
	if (!analysis) {
		return (
			<EmptyState
				title="Analysis Not Available"
				description="AI analysis is not available for this blood test."
				icon={<InfoCircleOutlined style={{ fontSize: 32 }} />}
			/>
		);
	}

	if (analysis.status === "processing") {
		return (
			<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
				<InlineLoading message="Your results are being analysed" />
				<Text type="secondary" style={{ fontSize: 12 }}>This can take a few minutes. Please check back shortly.</Text>
			</div>
		);
	}

	// A historical collection lifted out of a cumulative report: only the newest is
	// analysed, so this is an outcome to state plainly, never an error.
	if (analysis.status === "not_analysed") {
		return (
			<EmptyState
				title="Results imported from a cumulative report"
				description="No AI analysis was run for this collection — analysis runs on the most recent collection only."
				icon={<InfoCircleOutlined style={{ fontSize: 32 }} />}
			/>
		);
	}

	// 'failed', or any present-but-non-completed/unknown status — treat as an
	// error rather than silently pretending nothing was ever attempted.
	return (
		<EmptyState
			title="There was an error analysing these results"
			description="Please contact your administrator."
			icon={<WarningOutlined style={{ fontSize: 32, color: "var(--mg-danger)" }} />}
		/>
	);
};

export default BloodAnalysisStateNotice;
